// src/pages/PublicPostPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Avatar,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    Button,
    Divider,
    Paper,
    useTheme,
    useMediaQuery,
    AppBar,
    Toolbar,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ShareIcon from '@mui/icons-material/Share';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SchoolIcon from '@mui/icons-material/School';
import { useTranslation } from 'react-i18next';
import {
    getPublicPostById,
    getPublicPostComments,
    PublicPost,
    PublicComment,
} from '../services/publicService';
import { SHARE_CONFIG } from '../config/app';

// Configuration des types de post
const POST_TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    reflection: { label: 'Réflexion', icon: <LightbulbOutlinedIcon fontSize="small" />, color: '#2196F3' },
    prayer: { label: 'Prière', icon: <AutoAwesomeIcon fontSize="small" />, color: '#9C27B0' },
    testimony: { label: 'Témoignage', icon: <FavoriteIcon fontSize="small" />, color: '#E91E63' },
    teaching: { label: 'Enseignement', icon: <SchoolIcon fontSize="small" />, color: '#FF9800' },
};

// Composant pour afficher un commentaire
const CommentCard: React.FC<{ comment: PublicComment; isReply?: boolean }> = ({ comment, isReply = false }) => {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1.5,
                py: 1.5,
                pl: isReply ? 4 : 0,
                borderBottom: isReply ? 'none' : '1px solid',
                borderColor: 'divider',
            }}
        >
            <Avatar
                src={comment.user?.profile_picture}
                sx={{ width: isReply ? 28 : 36, height: isReply ? 28 : 36 }}
            >
                {comment.user?.name?.charAt(0) || '?'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
                <Box sx={{ bgcolor: '#f0f2f5', borderRadius: 2, px: 1.5, py: 1, display: 'inline-block' }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {comment.user?.name || 'Anonyme'}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {comment.content}
                    </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.5, pl: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        {formatDate(comment.created_at)}
                    </Typography>
                    {comment.reactions_count > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            ❤️ {comment.reactions_count}
                        </Typography>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

const PublicPostPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [post, setPost] = useState<PublicPost | null>(null);
    const [comments, setComments] = useState<PublicComment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tryingToOpenApp, setTryingToOpenApp] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;

            try {
                setLoading(true);
                setError(null);

                const postId = parseInt(id, 10);
                const [postData, commentsData] = await Promise.all([
                    getPublicPostById(postId),
                    getPublicPostComments(postId),
                ]);

                setPost(postData);
                setComments(commentsData);
            } catch (err: any) {
                console.error('Error fetching post:', err);
                if (err.response?.status === 404) {
                    setError('Ce post n\'existe pas ou a été supprimé.');
                } else {
                    setError('Erreur lors du chargement du post.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    // Essayer d'ouvrir l'app, sinon rediriger vers le store
    const handleOpenInApp = () => {
        if (!id) return;

        setTryingToOpenApp(true);
        const deepLink = SHARE_CONFIG.getPostDeepLink(parseInt(id, 10));
        const storeUrl = SHARE_CONFIG.getStoreUrl();

        // Créer un iframe caché pour essayer le deep link
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        // Timer pour détecter si l'app n'est pas installée
        const timeout = setTimeout(() => {
            // Si on est encore sur la page après 2.5s, l'app n'est pas installée
            window.location.href = storeUrl;
        }, 2500);

        // Écouter si on quitte la page (app ouverte)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                clearTimeout(timeout);
                setTryingToOpenApp(false);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Essayer d'ouvrir le deep link
        window.location.href = deepLink;

        // Cleanup après quelques secondes
        setTimeout(() => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.body.removeChild(iframe);
            setTryingToOpenApp(false);
        }, 3000);
    };

    // Télécharger l'app directement
    const handleDownloadApp = () => {
        window.open(SHARE_CONFIG.getStoreUrl(), '_blank');
    };

    // Partager le lien
    const handleShare = async () => {
        const shareUrl = window.location.href;
        const shareText = post?.title || 'Voir ce post sur Zemen';

        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareText,
                    text: post?.content?.substring(0, 100) + '...',
                    url: shareUrl,
                });
            } catch (err) {
                console.log('Share cancelled');
            }
        } else {
            navigator.clipboard.writeText(shareUrl);
            alert('Lien copié !');
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const getEthiopianDate = () => {
        if (!post) return '';
        const months = [
            'Meskerem', 'Tikimt', 'Hidar', 'Tahsas', 'Tir', 'Yekatit',
            'Megabit', 'Miyazya', 'Ginbot', 'Sene', 'Hamle', 'Nehase', 'Pagume'
        ];
        const monthName = months[post.ethiopian_month - 1] || `Month ${post.ethiopian_month}`;
        return `${post.ethiopian_day} ${monthName}${post.ethiopian_year ? ` ${post.ethiopian_year}` : ''}`;
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !post) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                <AppBar position="static" elevation={0} sx={{ bgcolor: '#2563eb' }}>
                    <Toolbar>
                        <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>Zemen</Typography>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadApp}
                            sx={{ bgcolor: 'white', color: '#2563eb', '&:hover': { bgcolor: '#f0f0f0' } }}
                        >
                            Télécharger
                        </Button>
                    </Toolbar>
                </AppBar>
                <Container maxWidth="sm" sx={{ py: 4 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>{error || 'Post non trouvé'}</Alert>
                    <Button variant="outlined" onClick={() => navigate('/')}>Retour à l'accueil</Button>
                </Container>
            </Box>
        );
    }

    const postTypeConfig = POST_TYPE_CONFIG[post.post_type] || POST_TYPE_CONFIG.reflection;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Header */}
            <AppBar position="sticky" elevation={1} sx={{ bgcolor: '#2563eb' }}>
                <Toolbar>
                    <Typography variant="h5" fontWeight={700} sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
                        Zemen
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleOpenInApp}
                            disabled={tryingToOpenApp}
                            sx={{
                                color: 'white',
                                borderColor: 'white',
                                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                            }}
                        >
                            {tryingToOpenApp ? 'Ouverture...' : 'Ouvrir dans l\'app'}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadApp}
                            sx={{ bgcolor: 'white', color: '#2563eb', fontWeight: 600, '&:hover': { bgcolor: '#f0f0f0' } }}
                        >
                            {isMobile ? 'App' : 'Télécharger'}
                        </Button>
                    </Stack>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md" sx={{ py: 3 }}>
                {/* Bannière "Ouvrir dans l'app" sur mobile */}
                {isMobile && (
                    <Paper
                        elevation={0}
                        sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: '#e3f2fd',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <PhoneAndroidIcon sx={{ color: '#2563eb' }} />
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600}>Zemen Calendar</Typography>
                                <Typography variant="caption" color="text.secondary">Meilleure expérience dans l'app</Typography>
                            </Box>
                        </Stack>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleOpenInApp}
                            disabled={tryingToOpenApp}
                            sx={{ bgcolor: '#2563eb' }}
                        >
                            Ouvrir
                        </Button>
                    </Paper>
                )}

                {/* Post Card */}
                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    {/* Post Header */}
                    <CardContent sx={{ pb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                                src={post.user?.profile_picture}
                                sx={{ width: 48, height: 48, bgcolor: '#2563eb' }}
                            >
                                {post.user?.name?.charAt(0) || post.author_name?.charAt(0) || '?'}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {post.user?.name || post.author_name || 'Anonyme'}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(post.created_at)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">•</Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <CalendarTodayIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {getEthiopianDate()}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                            <Chip
                                icon={postTypeConfig.icon as React.ReactElement}
                                label={postTypeConfig.label}
                                size="small"
                                sx={{ bgcolor: `${postTypeConfig.color}15`, color: postTypeConfig.color, fontWeight: 500 }}
                            />
                        </Stack>
                    </CardContent>

                    {/* Post Content */}
                    <CardContent sx={{ pt: 1 }}>
                        {post.title && (
                            <Typography variant="h6" fontWeight={600} gutterBottom>{post.title}</Typography>
                        )}
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: 'text.primary' }}>
                            {post.content}
                        </Typography>
                    </CardContent>

                    {/* Media */}
                    {post.media && post.media.length > 0 && (
                        <Box sx={{ px: 2, pb: 2 }}>
                            {post.media.map((media, index) => (
                                <Box key={media.id || index} sx={{ mb: 1 }}>
                                    {media.file_type === 'image' && (
                                        <CardMedia
                                            component="img"
                                            image={media.file_url}
                                            alt={media.alt_text || 'Image'}
                                            sx={{ borderRadius: 2, maxHeight: 400, objectFit: 'cover' }}
                                        />
                                    )}
                                    {media.file_type === 'video' && (
                                        <Box component="video" controls sx={{ width: '100%', borderRadius: 2, maxHeight: 400 }}>
                                            <source src={media.file_url} />
                                        </Box>
                                    )}
                                    {media.caption && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {media.caption}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Stats & Actions */}
                    <Box sx={{ px: 2, pb: 2 }}>
                        <Divider sx={{ mb: 1.5 }} />
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={3}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <FavoriteIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                                    <Typography variant="body2" fontWeight={500}>{post.reactions_count || 0}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                    <Typography variant="body2" fontWeight={500}>{post.comments_count || 0}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <ShareIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                    <Typography variant="body2" fontWeight={500}>{post.shares_count || 0}</Typography>
                                </Stack>
                            </Stack>
                            <Button size="small" startIcon={<ShareIcon />} onClick={handleShare} sx={{ textTransform: 'none' }}>
                                Partager
                            </Button>
                        </Stack>
                    </Box>
                </Card>

                {/* Comments Section */}
                <Paper elevation={0} sx={{ mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight={600}>Commentaires ({comments.length})</Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                        {comments.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                <Typography color="text.secondary">Aucun commentaire pour le moment</Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOpenInApp}
                                    sx={{ mt: 2 }}
                                >
                                    Commenter dans l'app
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                {comments.map((comment) => (
                                    <CommentCard key={comment.id} comment={comment} />
                                ))}
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleOpenInApp}
                                    sx={{ mt: 2 }}
                                >
                                    Voir plus et commenter dans l'app
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* CTA Download App */}
                <Paper
                    elevation={0}
                    sx={{
                        mt: 2,
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #2563eb 0%, #6366f1 100%)',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" fontWeight={700} color="white" gutterBottom>
                        Rejoignez la communauté Zemen
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.9)" sx={{ mb: 2 }}>
                        Téléchargez l'application pour interagir, commenter et partager vos réflexions spirituelles.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleOpenInApp}
                            sx={{ bgcolor: 'white', color: '#2563eb', fontWeight: 600, '&:hover': { bgcolor: '#f0f0f0' } }}
                        >
                            Ouvrir l'app
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadApp}
                            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Télécharger
                        </Button>
                    </Stack>
                </Paper>

                {/* Footer */}
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        Zemen - Le calendrier spirituel éthiopien
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default PublicPostPage;
